import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FlightSegment, useAddTripItem, TripData } from "@/hooks/useTrips";
import { useAutoEnrichFlight } from "@/hooks/useFlightEnrichment";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const flightSchema = z.object({
  airline: z.string().min(1, "Airline is required"),
  flight_number: z.string().min(1, "Flight number is required"),
  departure_airport: z.string().min(1, "Departure airport is required"),
  arrival_airport: z.string().min(1, "Arrival airport is required"),
  departure_time: z.string().min(1, "Departure time is required"),
  arrival_time: z.string().min(1, "Arrival time is required"),
  cabin_class: z.string().optional(),
  confirmation_number: z.string().optional(),
  aircraft: z.string().optional(),
  stops: z.coerce.number().min(0).optional(),
  meals: z.string().optional(),
});

type FlightFormData = z.infer<typeof flightSchema>;

interface AddFlightDialogProps {
  tripId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AddFlightDialog = ({
  tripId,
  open,
  onOpenChange,
}: AddFlightDialogProps) => {
  const addItem = useAddTripItem();
  const autoEnrich = useAutoEnrichFlight();

  const form = useForm<FlightFormData>({
    resolver: zodResolver(flightSchema),
    defaultValues: {
      airline: "",
      flight_number: "",
      departure_airport: "",
      arrival_airport: "",
      departure_time: "",
      arrival_time: "",
      cabin_class: "",
      confirmation_number: "",
      aircraft: "",
      stops: 0,
      meals: "",
    },
  });

  const onSubmit = async (data: FlightFormData) => {
    addItem.mutate(
      {
        tripId,
        itemType: "flights",
        item: data as FlightSegment,
      },
      {
        onSuccess: async () => {
          toast.success("Flight added");
          form.reset();
          onOpenChange(false);

          // Get the index of the newly added flight and auto-enrich
          try {
            const { data: trip } = await supabase
              .from("trips")
              .select("trip_data")
              .eq("id", tripId)
              .single();
            
            if (trip?.trip_data) {
              const tripData = trip.trip_data as TripData;
              const flights = tripData.flights || [];
              const newFlightIndex = flights.length - 1;
              
              if (newFlightIndex >= 0) {
                autoEnrich.mutate({
                  tripId,
                  flightIndex: newFlightIndex,
                  flight: flights[newFlightIndex],
                });
              }
            }
          } catch (err) {
            console.error("Failed to auto-enrich:", err);
          }
        },
        onError: (error) => {
          toast.error(`Failed to add: ${error.message}`);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Flight</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="airline"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Airline</FormLabel>
                    <FormControl>
                      <Input placeholder="United" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="flight_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Flight Number</FormLabel>
                    <FormControl>
                      <Input placeholder="UA123" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="departure_airport"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>From</FormLabel>
                    <FormControl>
                      <Input placeholder="JFK" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="arrival_airport"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>To</FormLabel>
                    <FormControl>
                      <Input placeholder="LAX" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="departure_time"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Departure Time</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="arrival_time"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Arrival Time</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="cabin_class"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cabin Class</FormLabel>
                    <FormControl>
                      <Input placeholder="Economy" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmation_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirmation #</FormLabel>
                    <FormControl>
                      <Input placeholder="ABC123" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="aircraft"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Aircraft</FormLabel>
                    <FormControl>
                      <Input placeholder="777-300" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="stops"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Stops</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="meals"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Meals</FormLabel>
                    <FormControl>
                      <Input placeholder="Dinner, Breakfast" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={addItem.isPending}>
                {addItem.isPending ? "Adding..." : "Add Flight"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
